import { Download, FileArchive, FileText, Languages, ScrollText, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ReaderExportFormat } from '@/api/readerApi'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

export type ProjectExportFormatId = ReaderExportFormat | 'fountain' | 'pdf'

export interface ProjectExportFormat {
  id: ProjectExportFormatId
  enabled: boolean
  primary?: boolean
  exportFormat?: ReaderExportFormat
}

export const PROJECT_EXPORT_FORMATS: ProjectExportFormat[] = [
  { id: 'fdx', enabled: true, primary: true, exportFormat: 'fdx' },
  { id: 'bilingual-md', enabled: true, exportFormat: 'bilingual-md' },
  { id: 'bilingual-docx', enabled: true, exportFormat: 'bilingual-docx' },
  { id: 'epub3', enabled: true, exportFormat: 'epub3' },
  { id: 'md', enabled: true, exportFormat: 'md' },
  { id: 'txt', enabled: true, exportFormat: 'txt' },
  { id: 'fountain', enabled: true, exportFormat: 'fountain' },
  { id: 'pdf', enabled: true, exportFormat: 'pdf' },
]

interface ExportCenterProps {
  open: boolean
  exportingFormat: ReaderExportFormat | null
  onOpenChange: (open: boolean) => void
  onExport: (format: ReaderExportFormat) => void
}

function formatIcon(id: ProjectExportFormatId) {
  if (id === 'fdx') return <Sparkles className="size-4" />
  if (id === 'epub3') return <FileArchive className="size-4" />
  if (id === 'bilingual-md' || id === 'bilingual-docx') return <Languages className="size-4" />
  if (id === 'md') return <ScrollText className="size-4" />
  return <FileText className="size-4" />
}

function ExportCenterContent({ exportingFormat, onExport }: Omit<ExportCenterProps, 'open' | 'onOpenChange'>) {
  const { t } = useTranslation()

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        {PROJECT_EXPORT_FORMATS.map((item) => {
          const isExporting = item.exportFormat ? exportingFormat === item.exportFormat : false
          return (
            <div
              key={item.id}
              className={item.primary
                ? 'rounded-lg border border-primary/45 bg-primary/5 p-4'
                : 'rounded-lg border bg-card/70 p-4'}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-primary">{formatIcon(item.id)}</span>
                    <p className="text-sm font-semibold">{t(`reader.exportCenter.formats.${item.id}.title`)}</p>
                    {item.primary ? <Badge>{t('reader.exportCenter.primary')}</Badge> : null}
                    {!item.enabled ? <Badge variant="outline">{t('reader.exportCenter.comingSoon')}</Badge> : null}
                  </div>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">
                    {t(`reader.exportCenter.formats.${item.id}.description`)}
                  </p>
                  {item.id === 'fdx' ? (
                    <p className="mt-2 text-xs font-medium text-primary">{t('reader.exportCenter.goldenContract')}</p>
                  ) : null}
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <Button
                  type="button"
                  size="sm"
                  variant={item.primary ? 'default' : 'outline'}
                  disabled={!item.enabled || Boolean(exportingFormat)}
                  onClick={() => {
                    if (item.exportFormat) onExport(item.exportFormat)
                  }}
                >
                  <Download className={isExporting ? 'size-4 animate-pulse' : 'size-4'} />
                  {isExporting ? t('reader.exporting') : t('reader.exportCenter.exportAction')}
                </Button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ExportCenterFrame({
  exportingFormat,
  onExport,
  dialog,
}: Omit<ExportCenterProps, 'open' | 'onOpenChange'> & { dialog: boolean }) {
  const { t } = useTranslation()
  const header = dialog ? (
    <DialogHeader>
      <DialogTitle>{t('reader.exportCenter.title')}</DialogTitle>
      <DialogDescription>{t('reader.exportCenter.description')}</DialogDescription>
    </DialogHeader>
  ) : (
    <div>
      <h2 className="text-lg font-semibold">{t('reader.exportCenter.title')}</h2>
      <p className="mt-2 text-sm text-muted-foreground">{t('reader.exportCenter.description')}</p>
    </div>
  )

  return (
    <>
      {header}
      <ExportCenterContent exportingFormat={exportingFormat} onExport={onExport} />
    </>
  )
}

export function ExportCenter({ open, exportingFormat, onOpenChange, onExport }: ExportCenterProps) {
  const fallbackContent = <ExportCenterFrame dialog={false} exportingFormat={exportingFormat} onExport={onExport} />

  return (
    <>
      {typeof window === 'undefined' && open ? (
        <div data-export-center-ssr>{fallbackContent}</div>
      ) : null}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[min(720px,calc(100vh-2rem))] overflow-y-auto sm:max-w-3xl">
          <ExportCenterFrame dialog exportingFormat={exportingFormat} onExport={onExport} />
        </DialogContent>
      </Dialog>
    </>
  )
}
